import React, { useEffect, useRef, useState } from "react";
import { animated, useSpring, to } from "@react-spring/web";
import AnimatedRainbowBorder from "./AnimatedRainbowBorder";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import HowToVoteOutlinedIcon from "@mui/icons-material/HowToVoteOutlined";
import PetsOutlinedIcon from "@mui/icons-material/PetsOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import SelfImprovementOutlinedIcon from "@mui/icons-material/SelfImprovementOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

// Darker, saturated — legible on white background
const RAINBOW = [
  "#E0544A",
  "#B327C2",
  "#4F52E8",
  "#0891B2",
  "#16A34A",
  "#C57D00",
  "#D97706",
  "#E0544A",
  "#B327C2",
  "#4F52E8",
];

const ITEMS: { icon: React.ElementType; text: string }[] = [
  { icon: PeopleAltOutlinedIcon,       text: "Contribute to the internet. Collaborate when possible." },
  { icon: FavoriteBorderIcon,          text: "Share love, knowledge and opinions." },
  { icon: BugReportOutlinedIcon,       text: "Always give feedback. Report broken things." },
  { icon: LabelOutlinedIcon,           text: "Take NSFW tags seriously." },
  { icon: HowToVoteOutlinedIcon,       text: "Be political." },
  { icon: PetsOutlinedIcon,            text: "Share cat/dog memes. After all, the internet was made for it." },
  { icon: TouchAppOutlinedIcon,        text: 'Be mindful before pressing any button that says "Allow".' },
  { icon: SelfImprovementOutlinedIcon, text: "Correct your posture. Don't forget to blink. Stay hydrated." },
  { icon: LockOutlinedIcon,            text: "Never share your OTP." },
  { icon: PublicOutlinedIcon,          text: "Don't Zuck up the internet." },
];

const peekOut = { tx: 230, ty: -58, rz: -12, rx: 0 };
const peekIn = { tx: 0, ty: -50, rz: 0, rx: 0 };

export interface PeekingManifestoProps {
  /** Cookie screen: slide in from the right, float in Z, then settle like paper */
  cookieEntrance?: boolean;
  /** Wait before starting the slide (ms) — align with parent opacity reveal */
  entranceDelayMs?: number;
}

const PeekingManifesto: React.FC<PeekingManifestoProps> = ({
  cookieEntrance = false,
  entranceDelayMs = 0,
}) => {
  const [hovered, setHovered] = useState(false);
  const [entranceDone, setEntranceDone] = useState(!cookieEntrance);
  const skipHoverSyncRef = useRef(false);

  const offTx =
    typeof window !== "undefined"
      ? window.innerWidth * 1.6 // push well beyond the right edge so it's truly off-screen
      : 920;

  const [motion, api] = useSpring(() => ({
    tx: cookieEntrance ? offTx : peekOut.tx,
    ty: peekOut.ty,
    rx: 0,
    rz: peekOut.rz,
    lift: 0,
    scale: cookieEntrance ? 0.94 : 1,
    config: { tension: 160, friction: 26 },
  }));

  useEffect(() => {
    if (!cookieEntrance) return;

    let cancelled = false;

    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const runEntrance = () => {
      if (cancelled) return;

      if (reduceMotion) {
        api.start({
          tx: peekOut.tx,
          ty: peekOut.ty,
          rx: 0,
          rz: peekOut.rz,
          lift: 0,
          config: { duration: 1 },
          onRest: () => {
            if (cancelled) return;
            skipHoverSyncRef.current = true;
            setEntranceDone(true);
          },
        });
        return;
      }

      api.start({
        from: {
          tx: offTx,
          ty: peekOut.ty,
          rx: 0,
          rz: peekOut.rz * 0.5,
          lift: 0,
          scale: 0.94,
        },
        to: {
          tx: peekOut.tx,
          ty: peekOut.ty,
          rx: 0,
          rz: peekOut.rz,
          lift: 0,
          scale: 1,
        },
        // Softer, slower entrance for a subtle settle
        config: { tension: 140, friction: 30 },
        onRest: () => {
          if (cancelled) return;
          skipHoverSyncRef.current = true;
          setEntranceDone(true);
        },
      });
    };

    const delay = Math.max(0, entranceDelayMs);
    if (delay === 0) {
      runEntrance();
      return () => {
        cancelled = true;
      };
    }
    const id = window.setTimeout(runEntrance, delay);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [cookieEntrance, entranceDelayMs, api, offTx]);

  useEffect(() => {
    if (!entranceDone) return;
    if (skipHoverSyncRef.current) {
      skipHoverSyncRef.current = false;
      return;
    }
    api.start({
      tx: hovered ? peekIn.tx : peekOut.tx,
      ty: hovered ? peekIn.ty : peekOut.ty,
      rz: hovered ? peekIn.rz : peekOut.rz,
      rx: hovered ? peekIn.rx : peekOut.rx,
      scale: hovered ? 1 : 1,
      config: { tension: 280, friction: 28 },
    });
  }, [hovered, entranceDone, api]);

  return (
    <>
      <div
        className="fixed right-0 z-50"
        style={{ top: "18vh", width: "128px", height: "64vh" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />

      <div
        className="fixed top-1/2 right-5 z-50 pointer-events-none"
        style={{
          perspective: "1100px",
          perspectiveOrigin: "100% 50%",
        }}
      >
        <animated.div
          className="pointer-events-none"
          style={{
            transformOrigin: "90% 50%",
            transform: to(
              [motion.tx, motion.ty, motion.rx, motion.rz, motion.lift, motion.scale],
              (tx, ty, rx, rz, lift, scale) =>
                `translate3d(${tx}px, ${ty}%, ${-18 * lift}px) rotateX(${rx}deg) rotate(${rz}deg) scale(${scale})`
            ),
            filter: motion.lift.to(
              (l) => `drop-shadow(0 ${12 + l * 28}px ${28 + l * 36}px rgba(0,0,0,${0.35 + l * 0.12}))`
            ),
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <AnimatedRainbowBorder
            style={{ width: "390px" }}
            shadow="0 28px 80px rgba(0,0,0,0.6)"
            glowOpacity={0.5}
          >
            <div className="px-6 pt-5 pb-3 shrink-0">
              <p className="gilroy-regular text-[10px] tracking-[0.18em] uppercase text-black/30 mb-1">
                Dearest Internet
              </p>
              <h1 className="text-black text-[18px] leading-tight" style={{ fontFamily: "'Kurdis Bold'" }}>
                Our Better Internet<br />Manifesto
              </h1>
            </div>

            <div
              className="mx-6 shrink-0"
              style={{
                height: "1.5px",
                background:
                  "linear-gradient(90deg,#F4958E,#FFAAF7,#9193FC,#B4F6FE,#B0FFC0,#FEFF9D,#F3D9A0)",
                opacity: 0.5,
              }}
            />

            <ul className="px-6 pt-5 pb-6 space-y-3.5">
              {ITEMS.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Icon sx={{ fontSize: 17, marginTop: "2px", flexShrink: 0, color: RAINBOW[i] }} />
                  <span className="gilroy-medium text-black text-[14px] leading-snug">
                    {text}
                  </span>
                </li>
              ))}
            </ul>
          </AnimatedRainbowBorder>
        </animated.div>
      </div>
    </>
  );
};

export default PeekingManifesto;
